// =============================================================================
// AIUsageSection.js — Phase 82
//
// Wired to backend GET /api/usage/summary which returns the current month's
// used/caps map keyed by feature (pdf_parse, receipt_scan). We display the
// real numbers alongside the Figma feature breakdown; features that aren't
// present in the response are simply omitted (no fabricated data).
// =============================================================================
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { SectionHeader, SettingsCard } from './primitives';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURE_LABELS = {
  pdf_parse:    'PDF statement parsing',
  receipt_scan: 'Receipt scan & extract',
  briefing:     'Financial Briefing generation',
  patterns:     'Pattern detection & insights',
  categorise:   'Transaction categorisation',
};

export default function AIUsageSection() {
  const { session } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session?.access_token) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/usage/summary`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
          timeout: 10000,
        });
        if (!cancelled) setUsage(r.data);
      } catch { /* non-fatal — usage stays null and we render an empty state */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [session?.access_token]);

  const used = usage?.used || {};
  const caps = usage?.caps || {};
  const totalUsed = Object.values(used).reduce((s, n) => s + Number(n || 0), 0);
  const totalCap  = Object.values(caps).reduce((s, n) => s + (Number(n) >= 999 ? 0 : Number(n || 0)), 0);
  const pct = totalCap > 0 ? Math.min(100, Math.round((totalUsed / totalCap) * 100)) : 0;

  // v3.7.14 Phase 84.31 Part 8 — Robust month label.
  // Previously we did `new Date(usage.month + '-01')`. When usage.month
  // was missing, that produced "undefined-01" and .toLocaleDateString
  // returned the literal string "Invalid Date". Now we validate before
  // formatting and fall back to the current calendar month.
  const monthLabel = (() => {
    try {
      const raw = usage?.month;
      const src = raw && /^\d{4}-\d{2}$/.test(raw)
        ? new Date(`${raw}-01T00:00:00Z`)
        : new Date();
      if (Number.isNaN(src.getTime())) return '';
      return src.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    } catch { return ''; }
  })();

  const features = Object.keys(used).map((k) => ({
    key: k,
    label: FEATURE_LABELS[k] || k,
    calls: Number(used[k] || 0),
    cap: Number(caps[k] || 0),
  })).filter((f) => f.calls > 0 || f.cap > 0);

  return (
    <div className="space-y-5" data-testid="settings-ai-section">
      <SectionHeader title="AI Usage" subtitle="These are your monthly AI request allowances, included in your plan. Nothing here costs you extra — the counts reset on the 1st every month." />

      {/* Monthly usage */}
      <SettingsCard className="px-6 py-6" testId="settings-ai-summary">
        <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-2">
              This month{monthLabel ? ` — ${monthLabel}` : ''}
            </p>
            <p className="font-display text-3xl font-medium text-charcoal" style={{ letterSpacing: '-0.02em' }}>
              <span data-testid="settings-ai-total-used" className="tabular-nums">{totalUsed.toLocaleString()}</span>
              {totalCap > 0 && (
                <span className="text-base text-muted-foreground font-sans font-normal ml-2 tabular-nums">
                  / {totalCap.toLocaleString()} requests
                </span>
              )}
            </p>
          </div>
          {totalCap > 0 && (
            <span
              className={`text-2xl font-display font-medium ${pct > 80 ? 'text-red-600 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400'}`}
              data-testid="settings-ai-pct"
            >
              {pct}%
            </span>
          )}
        </div>
        {totalCap > 0 && (
          <>
            <div className="h-2 bg-offwhite rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${pct > 80 ? 'bg-red-600 dark:bg-red-400' : 'bg-emerald-700 dark:bg-emerald-400'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2.5">
              {Math.max(0, totalCap - totalUsed).toLocaleString()} requests remaining · Resets on the 1st of next month.
            </p>
          </>
        )}
        {totalCap === 0 && !loading && features.length === 0 && (
          <p className="text-sm text-muted-foreground">No AI features have been used this month.</p>
        )}
      </SettingsCard>

      {/* Feature breakdown */}
      {features.length > 0 && (
        <SettingsCard className="px-6 py-5" testId="settings-ai-breakdown">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-4">Usage by feature</p>
          <div className="space-y-4">
            {features.map((f) => {
              const share = totalUsed > 0 ? Math.min(100, (f.calls / totalUsed) * 100) : 0;
              const isCap = f.cap > 0 && f.calls >= f.cap && f.cap < 999;
              return (
                <div key={f.key} data-testid={`settings-ai-feature-${f.key}`}>
                  <div className="flex items-center justify-between mb-1.5 gap-2">
                    <span className="text-sm text-charcoal">{f.label}</span>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="tabular-nums">{f.calls.toLocaleString()} requests</span>
                      {f.cap > 0 && f.cap < 999 && (
                        <span className="font-medium text-charcoal tabular-nums">/ {f.cap.toLocaleString()}</span>
                      )}
                      {isCap && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[hsl(var(--gold))]/20 border border-[hsl(var(--gold))]/40 text-foreground/70">
                          cap reached
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="h-1 bg-offwhite rounded-full overflow-hidden">
                    <div className="h-full rounded-full bg-evergreen/50" style={{ width: `${share}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </SettingsCard>
      )}

      {/* AI transparency */}
      <SettingsCard className="px-6 py-5">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70 mb-4">How 2120 uses AI</p>
        <div className="space-y-3 text-sm text-muted-foreground/90 leading-relaxed">
          <p>AI in 2120 is used to identify meaningful financial patterns, generate plain-language summaries of your position, and suggest contextual next steps.</p>
          <p>Your financial data is processed to generate insights. It is not used to train AI models, shared with third parties, or used to make automated financial decisions on your behalf.</p>
          <p>All AI-generated suggestions are clearly labelled and can be dismissed at any time.</p>
        </div>
      </SettingsCard>
    </div>
  );
}
